/*
 * Copyright (c) 2022-2023 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { inject, Injectable } from '@angular/core';
import { FabThemeSetting } from '@fabric/components/theme';

import { LocalStorageService } from '../local-storage/local-storage.service';

@Injectable({
  providedIn: 'root',
})
export class SettingsService {
  private readonly themeSettingKey = 'fab.themeSetting';

  private defaults: Map<string, string> = new Map<string, string>([[this.themeSettingKey, 'system']]);

  private settings: Map<string, string> = new Map<string, string>();

  get themeSetting(): FabThemeSetting {
    return this.getSetting(this.themeSettingKey) as FabThemeSetting;
  }
  set themeSetting(mode: FabThemeSetting) {
    this.setSetting(this.themeSettingKey, mode as string);
  }

  /** Use local storage to persist user settings */
  private readonly storageService: LocalStorageService = inject(LocalStorageService);

  /**
   * Get a setting from our settings store.  If it doesn't exist, then grab it from localStorage and set it in our store
   * @param key - setting key
   * @returns setting value
   */
  private getSetting(key: string): string | undefined {
    if (!this.settings.get(key)) {
      const setting = this.storageService.retrieve(key) ?? this.defaults.get(key);
      if (setting) {
        this.settings.set(key, setting);
      }
    }
    return this.settings.get(key);
  }

  /**
   * Set the value in local storage and our own store
   * @param key setting key
   * @param value setting value
   */
  private setSetting(key: string, value: string): void {
    this.settings.set(key, value);
    this.storageService.store(key, value);
  }
}
